import { useCallback, useEffect, useState } from 'react';

import { useTrackEmailChangeRequested } from '@air/analytics';
import { isAirror, TOO_MANY_REQUESTS } from '@air/errors';
import { useToasts } from '@air/provider-toast';

import { usePrivateAccountRequestEmailChangeMutation } from '~/components/v2/hooks/queries/mutations/usePrivateAccountRequestEmailChangeMutation';
import { usePendingEmailCooldownStore } from '~/components/v2/store/features/pending-email/usePendingEmailCooldownStore';
import { useAccountContext } from '~/providers/AccountProvider';

const ONE_SECOND_MS = 1000;

export type UsePendingEmailResendResult = {
  isResending: boolean;
  resend: () => void;
  secondsRemaining: number;
};

// Public arm-cooldown for non-resend entry points (e.g. the initial email
// change submit from MyAccountForm). Keeping a single shared cooldown
// prevents the "Save changes" and "Resend email" buttons from diverging.
export const startPendingEmailCooldown = () => usePendingEmailCooldownStore.getState().start();

/**
 * Subscribes to the shared pending-email cooldown and returns the remaining
 * seconds. Read-only — use this in surfaces that shouldn't also be able to
 * resend (e.g. the Save changes button).
 */
export const usePendingEmailCooldownSeconds = (): number => {
  const endsAt = usePendingEmailCooldownStore((s) => s.endsAt);
  // Read Date.now() at render rather than caching it in state — a cached
  // `now` goes stale between cooldowns and flashes an inflated countdown
  // on the next arm.
  const [, setTick] = useState(0);

  useEffect(() => {
    if (endsAt === 0) {
      return;
    }
    const interval = setInterval(() => {
      if (Date.now() >= endsAt) {
        clearInterval(interval);
        // Reset on natural expiry so a later mount doesn't re-read a stale
        // non-zero endsAt and spin up a one-tick interval just to clear it.
        usePendingEmailCooldownStore.getState().clear();
        return;
      }
      setTick((t) => t + 1);
    }, ONE_SECOND_MS);

    return () => clearInterval(interval);
  }, [endsAt]);

  return Math.max(0, Math.ceil((endsAt - Date.now()) / ONE_SECOND_MS));
};

/**
 * Shared resend-verification-email behavior for the pending-email UI
 * surfaces. Bundles the mutation, analytics event, the
 * TOO_MANY_REQUESTS → success coercion, and the 60s cooldown countdown.
 */
export const usePendingEmailResend = (): UsePendingEmailResendResult => {
  const { showToast } = useToasts();
  const { data: account } = useAccountContext();
  const { trackEmailChangeRequested } = useTrackEmailChangeRequested();
  const { mutate: requestEmailChange, isPending: isResending } = usePrivateAccountRequestEmailChangeMutation();

  const secondsRemaining = usePendingEmailCooldownSeconds();

  const resend = useCallback(() => {
    const pendingEmail = account?.pendingEmail;
    const userId = account?.id;
    // Read the cooldown directly from the store rather than depending on
    // `secondsRemaining` — that ticks every second and would re-create
    // `resend` on every tick.
    const isCoolingDown = usePendingEmailCooldownStore.getState().endsAt > Date.now();

    if (!pendingEmail || !userId || isResending || isCoolingDown) {
      return;
    }

    // Start the cooldown before the request resolves so a near-simultaneous
    // click from the other surface bails on the isCoolingDown check.
    usePendingEmailCooldownStore.getState().start();

    requestEmailChange(
      { email: pendingEmail },
      {
        onSuccess: () => {
          trackEmailChangeRequested({ user_id: userId, new_email: pendingEmail });
        },
        onError: (error) => {
          // The backend rate-limits resends. A TOO_MANY_REQUESTS response
          // means the email is already on its way — keep the cooldown and
          // fire the analytics event, same as the success path.
          if (isAirror(error) && error.type === TOO_MANY_REQUESTS.type) {
            trackEmailChangeRequested({ user_id: userId, new_email: pendingEmail });
            return;
          }

          usePendingEmailCooldownStore.getState().clear();
          showToast('Failed to resend verification email', { color: 'red' });
        },
      },
    );
  }, [account?.id, account?.pendingEmail, isResending, requestEmailChange, showToast, trackEmailChangeRequested]);

  return { isResending, resend, secondsRemaining };
};
